import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { tap, catchError, switchMap, first } from 'rxjs/operators';
import { BehaviorSubject, of, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { RequiredDocumentService } from '@shared/services/api/required-document.service';
import { RequiredDocumentFormService } from '@shared/services/api/required-document-form.service';
import { ValuesService } from '@shared/services/api/values.service';
import { RequiredDocument } from '@shared/entities/requiredDocument';
import { Attachment } from '@shared/entities/attachment';

interface NamedOption {
  name: string;
  id: string;
}

@Component({
  selector: 'app-required-document-form',
  templateUrl: './required-document-form.component.html',
  styleUrl: './required-document-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequiredDocumentFormComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private router = inject(Router);
  private requiredDocumentService = inject(RequiredDocumentService);
  private requiredDocumentFormService = inject(RequiredDocumentFormService);
  private _valuesService = inject(ValuesService);

  isLight = true;
  isEdit = false;
  isLoading = false;

  form!: FormGroup;

  private _docId: string | null = null;
  public _requiredDocument: RequiredDocument = new RequiredDocument();

  template: File | null = null;
  existingTemplate: Attachment | null = null;

  isSuccess = false;
  isError = false;
  errorMessage = '';
  deleteSuccess = false;
  deleteError = false;
  isDeletingOperation = false;
  
  showDeleteModal = false;
  deleteModalMessage = '';

  private _typologySubject = new BehaviorSubject<NamedOption[]>([]);
  typologyOptions$ = this._typologySubject.asObservable();

  fetchTypologies = (searchTerm: string): Observable<NamedOption[]> => {
    const options = this._typologySubject.getValue();
    if (!searchTerm) return of(options);
    const term = searchTerm.toLowerCase();
    return of(options.filter((o) => o.name.toLowerCase().includes(term)));
  };

  constructor() {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      reqDocName: ['', Validators.required],
      displayOrder: [1, [Validators.required, Validators.min(1)]],
      customerCanUpload: [false, Validators.required],
      uploadablePeriodically: [false, Validators.required],
      associatedToMachine: [false, Validators.required],
      machineTypologies: [[]],
      notes: [''],
      template: [undefined]
    });
  }

  ngOnInit(): void {
    this.setupRouteSubscription();
    this.setupFormValueChanges();
    this.loadMachineTypologies();
  }

  private loadMachineTypologies(): void {
    this._valuesService.getMachineTypes().pipe(
      first(),
      catchError(() => of(null))
    ).subscribe((response) => {
      if (response && Array.isArray(response.values)) {
        this._typologySubject.next(response.values.map((item: string) => ({ name: item, id: item })));
      }
    });
  }

  private setupRouteSubscription(): void {
    this.route.params
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((params) => {
          this._docId = params['id'] || null;
          this.isEdit = !!this._docId;
          if (this.isEdit && this._docId) {
            this.loadRequiredDocumentData();
          }
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private setupFormValueChanges(): void {
    this.form
      .get('customerCanUpload')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        const canUpload = value === true || value === 'true';
        if (!canUpload) {
          this.form.get('uploadablePeriodically')?.setValue(false);
        }
        this._cdr.markForCheck();
      });
  }

  private convertToBoolean(value: any): boolean {
    return value === true || value === 'true';
  }

  private loadRequiredDocumentData(): void {
    if (!this._docId) {
      this.isLoading = false;
      this._cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this._cdr.markForCheck();

    this.requiredDocumentFormService
      .findByFilter(this._docId)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap((document) =>
          document ? of(document) : this.requiredDocumentFormService.findRequiredDocument(this._docId!)
        ),
        tap((document) => {
          if (document) {
            this._requiredDocument = document;
            this.patchFormValues(document);
          }
          this.isLoading = false;
          this._cdr.markForCheck();
        }),
        catchError(() => {
          this.isLoading = false;
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  private patchFormValues(document: RequiredDocument): void {
    const options = this._typologySubject.getValue();
    const typologyValues = document.machineTypologies
      ? document.machineTypologies.map((id) => options.find((o) => o.id === id) || { id, name: id })
      : [];
    this.form.patchValue({
      reqDocName: document.reqDocName || '',
      displayOrder: document.displayOrder ? Math.max(1, document.displayOrder) : 1,
      customerCanUpload: document.customerCanUpload !== undefined ? document.customerCanUpload : false,
      uploadablePeriodically: document.uploadablePeriodically !== undefined ? document.uploadablePeriodically : false,
      associatedToMachine: document.associatedToMachine !== undefined ? document.associatedToMachine : false,
      machineTypologies: typologyValues,
      notes: document.notes || ''
    });
    this.handleExistingTemplate(document);
  }

  private handleExistingTemplate(document: RequiredDocument): void {
    if (document.template) {
      this.existingTemplate = document.template;
    } else if (document.templateId) {
      this.existingTemplate = {
        id: document.templateId,
        fileName: 'Template File'
      } as Attachment;
    }

    if (this.existingTemplate) {
      this.form.get('template')?.setValidators(null);
      this.form.get('template')?.updateValueAndValidity();
    }
  }

  isAttachmentsValid(): boolean {
    return true;
  }

  onTemplateFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.template = input.files[0];
      this.form.get('template')?.setErrors(null);
    }
  }

  getYear() {
    return new Date().getFullYear();
  }

  removeTemplateAttachment(): void {
    if (this.template) {
      this.template = null;
      this.form.get('template')?.setValue(undefined);
    } else {
      this.existingTemplate = null;
    }

    this.form.get('template')?.markAsDirty();
    this.form.get('template')?.updateValueAndValidity();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.resetStateFlags();

    const doc = this.buildDocumentFromForm();
    const hasNewAttachments = !!this.template;

    if (hasNewAttachments) {
      this.submitWithAttachments(doc);
    } else {
      this.submitWithoutAttachments(doc);
    }
  }

  private buildDocumentFromForm(): RequiredDocument {
    const doc = new RequiredDocument();
    doc.name = this.form.value.reqDocName;
    doc.displayOrder = parseInt(this.form.value.displayOrder, 10) || 1;
    doc.customerCanUpload = this.convertToBoolean(this.form.value.customerCanUpload);
    doc.uploadablePeriodically = this.convertToBoolean(this.form.value.uploadablePeriodically);
    doc.associatedToMachine = this.convertToBoolean(this.form.value.associatedToMachine);
    const selectedTypologies: NamedOption[] = this.form.value.machineTypologies || [];
    doc.machineTypologies = selectedTypologies.map((o) => o.id);
    doc.notes = this.form.value.notes;

    if (this.isEdit && this._docId) {
      doc.id = this._docId;
      doc.templateId = this.existingTemplate ? this._requiredDocument?.templateId : '';
    }

    delete doc.template;
    return doc;
  }

  private submitWithAttachments(doc: RequiredDocument): void {
    const files: Record<string, File> = {};
    if (this.template) {
      files['template'] = this.template;
    }

    const request =
      this.isEdit && this._docId
        ? this.requiredDocumentFormService.updateRequiredDocumentWithAttachments(this._docId, doc, files)
        : this.requiredDocumentFormService.saveRequiredDocumentWithAttachments(doc, files);

    this.handleSubmitRequest(request);
  }

  private submitWithoutAttachments(doc: RequiredDocument): void {
    const request =
      this.isEdit && this._docId
        ? this.requiredDocumentFormService.editRequiredDocument(doc, this._docId)
        : this.requiredDocumentFormService.saveRequiredDocument(doc);

    this.handleSubmitRequest(request);
  }

  private handleSubmitRequest(request: any): void {
    request
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((response: any) => {
          this.isLoading = false;

          if (response && (response instanceof Error || response.error)) {
            this.isError = true;
            this.errorMessage = response.error?.message || 'Errore salvataggio documento';
            this.requiredDocumentService.refresh(1);
            this._cdr.markForCheck();
            return;
          }

          this.isSuccess = true;
          this.isError = false;
          this.requiredDocumentService.refreshFilters();
          this._cdr.markForCheck();
        }),
        catchError((error) => {
          this.isLoading = false;
          this.isError = true;
          this.errorMessage = error?.error?.message || 'Errore salvataggio documento';
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  toggleRequiredDocumentActivation(): void {
    if (!this._docId || !this._requiredDocument) {
      return;
    }

    this.isLoading = true;
    this.isDeletingOperation = false;
    this.resetStateFlags();

    const doc = this.buildToggleDocument();

    this.requiredDocumentFormService
      .editRequiredDocument(doc, this._docId)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((response: any) => {
          this.isLoading = false;

          if (response && !response.error) {
            this.isSuccess = true;
            this.deleteSuccess = true;
            this.requiredDocumentService.refreshFilters();
          } else {
            this.isError = true;
            this.deleteError = true;
            this.errorMessage = this.extractErrorMessage(response);
          }
          this._cdr.markForCheck();
        }),
        catchError((error) => {
          this.isLoading = false;
          this.isError = true;
          this.deleteError = true;
          this.errorMessage = this.extractErrorMessage(error?.error || error);
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  openDeleteModal(): void {
    this.deleteModalMessage = '';
    this.showDeleteModal = true;
    this._cdr.markForCheck();
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.deleteModalMessage = '';
    this._cdr.markForCheck();
  }

  confirmDelete(): void {
    if (!this._docId) {
      return;
    }

    this.isLoading = true;
    this.isDeletingOperation = true;
    this.showDeleteModal = false;
    this.resetStateFlags();

    this.requiredDocumentFormService
      .deleteRequiredDocument(this._docId)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((response: any) => {
          this.isLoading = false;

          if (response === true) {
            // Success
            this.isSuccess = true;
            this.deleteSuccess = true;
            this.requiredDocumentService.refreshFilters();
          } else if (response && response.isError && response.error) {
            // Error response from service
            this.isError = true;
            this.deleteError = true;
            this.deleteModalMessage = this.extractErrorMessage(response.error);
            this.errorMessage = this.deleteModalMessage;
            this.requiredDocumentService.refresh(1);
          } else {
            // Unexpected response
            this.isError = true;
            this.deleteError = true;
            this.deleteModalMessage = 'Error deleting required document';
            this.errorMessage = this.deleteModalMessage;
          }
          this._cdr.markForCheck();
        }),
        catchError((error) => {
          this.isLoading = false;
          this.isError = true;
          this.deleteError = true;
          this.errorMessage = this.extractErrorMessage(error?.error || error);
          this.deleteModalMessage = this.errorMessage;
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  private buildToggleDocument(): RequiredDocument {
    const doc = new RequiredDocument();
    doc.id = this._docId!;
    doc.name = this._requiredDocument.reqDocName || this._requiredDocument.name;
    doc.displayOrder = this._requiredDocument.displayOrder || 1;
    doc.customerCanUpload = this._requiredDocument.customerCanUpload || false;
    doc.uploadablePeriodically = this._requiredDocument.uploadablePeriodically || false;
    doc.associatedToMachine = this._requiredDocument.associatedToMachine || false;
    doc.notes = this._requiredDocument.notes || '';
    doc.templateId = this._requiredDocument.templateId;
    doc.machineTypologies = this._requiredDocument.machineTypologies;
    // Toggle the active state: if currently active, deactivate it; if inactive, activate it
    doc.active = this._requiredDocument.active !== undefined ? !this._requiredDocument.active : true;
    return doc;
  }

  isDocumentActive(): boolean {
    return this._requiredDocument?.active !== false;
  }

  getToggleButtonText(): string {
    return this.isDocumentActive() ? 'REQ_DOC_FORM.DEACTIVATE_BUTTON' : 'REQ_DOC_FORM.ACTIVATE_BUTTON';
  }

  getToggleSuccessTitle(): string {
    return this.isDocumentActive() ? 'REQ_DOC_FORM.DEACTIVATE_SUCCESS' : 'REQ_DOC_FORM.ACTIVATE_SUCCESS';
  }

  getToggleSuccessDescription(): string {
    return this.isDocumentActive()
      ? 'REQ_DOC_FORM.DEACTIVATED_DOC_DESCRIPTION'
      : 'REQ_DOC_FORM.ACTIVATED_DOC_DESCRIPTION';
  }

  getToggleErrorTitle(): string {
    return this.isDocumentActive() ? 'REQ_DOC_FORM.DEACTIVATE_ERROR_TITLE' : 'REQ_DOC_FORM.ACTIVATE_ERROR_TITLE';
  }

  getToggleErrorDescription(): string {
    return this.isDocumentActive()
      ? 'REQ_DOC_FORM.DEACTIVATE_ERROR_DESCRIPTION'
      : 'REQ_DOC_FORM.ACTIVATE_ERROR_DESCRIPTION';
  }

  getDeleteErrorTitle(): string {
    return 'REQ_DOC_FORM.DELETE_ERROR_TITLE';
  }

  getDeleteErrorDescription(): string {
    return 'REQ_DOC_FORM.DELETE_ERROR_DESCRIPTION';
  }

  goToList(): void {
    this.router.navigate(['/repository/required-documents']);
  }

  goBack(): void {
    this.location.back();
  }

  private resetStateFlags(): void {
    this.isSuccess = false;
    this.isError = false;
    this.deleteSuccess = false;
    this.deleteError = false;
    this.errorMessage = '';
    this.showDeleteModal = false;
  }

  private extractErrorMessage(response: any): string {
    if (!response) {
      return 'Unknown error occurred';
    }

    // Check if response is directly the error body with errorMessage field
    if (response.errorMessage && typeof response.errorMessage === 'string') {
      return response.errorMessage;
    }

    // Check for errorMessage from HttpErrorResponse.error (nested structure)
    if (response.error && response.error.errorMessage) {
      return response.error.errorMessage;
    }

    // Check for HTTP status code and return user-friendly message
    if (response.status) {
      const errorMessage = this.getHttpErrorMessage(response.status);
      return errorMessage;
    }

    if (response.message) {
      return response.message;
    }

    if (response.validation && response.validation.fields && response.validation.fields.length > 0) {
      const validationErrors = response.validation.fields
        .map((field: any) => `${field.field}: ${field.error}`)
        .join('\n');

      return `Validation errors:\n${validationErrors}`;
    }

    if (response.error && typeof response.error === 'string') {
      return response.error;
    }

    if (this.deleteError) {
      return 'Error deleting required document';
    }

    return 'An unexpected error occurred';
  }

  private getHttpErrorMessage(status: number): string {
    switch (status) {
      case 400:
        return 'Invalid request. Please check the data and try again.';
      case 401:
        return 'Authentication required. Please login again.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'Resource not found.';
      case 409:
        return 'Conflict: The document may have been modified or deleted by another user.';
      case 500:
        return 'Internal server error. Please try again later or contact support.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Error ${status}: ${this.getStatusText(status)}. Please try again.`;
    }
  }

  private getStatusText(status: number): string {
    const statusMap: Record<number, string> = {
      400: 'Bad Request',
      401: 'Unauthorized',
      403: 'Forbidden',
      404: 'Not Found',
      409: 'Conflict',
      500: 'Internal Server Error',
      503: 'Service Unavailable'
    };
    return statusMap[status] || 'Unknown Error';
  }
}
